import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Invoice from "@/components/Invoice";
import { Printer, Eye, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "open", "billed", "paid", "cancelled"];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [view, setView] = useState(null);

  const load = async (s) => {
    try {
      const params = s === "all" ? {} : { status_filter: s };
      const { data } = await api.get("/orders", { params });
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load(status);
  }, [status]);

  const cancel = async (o) => {
    if (!window.confirm(`Cancel ${o.order_no}?`)) return;
    try {
      await api.post(`/orders/${o.id}/cancel`);
      toast.success("Cancelled");
      load(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const statusBadge = (s) => {
    const map = {
      open: "bg-[#FBE9E2] text-[#D95D39]",
      billed: "bg-[#FFF4E0] text-[#E9A23B]",
      paid: "bg-[#E5EBE3] text-[#6A7F66]",
      cancelled: "bg-[#F4F2EE] text-[#7A7873]",
    };
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded-full capitalize ${map[s] || ""}`}
      >
        {s}
      </span>
    );
  };

  return (
    <div className="p-6 sm:p-8" data-testid="orders-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">
          Orders
        </h1>
        <p className="text-sm text-[#7A7873] mt-1">
          Full order history with invoice reprint and cancellation.
        </p>
      </div>

      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList className="bg-white border border-[#EBE8E2]">
          {STATUSES.map((s) => (
            <TabsTrigger
              key={s}
              value={s}
              className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39] capitalize"
              data-testid={`status-${s}`}
            >
              {s}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Order No</th>
              <th className="text-left px-4 py-2.5">Time</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Customer / Table</th>
              <th className="text-right px-4 py-2.5">Items</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr
                key={o.id}
                className="border-t border-[#EBE8E2]"
                data-testid={`order-row-${o.id}`}
              >
                <td className="px-4 py-2.5 font-mono-num font-semibold">
                  {o.order_no}
                </td>
                <td className="px-4 py-2.5 text-[#7A7873] text-xs">
                  {new Date(o.created_at).toLocaleString("en-IN")}
                </td>
                <td className="px-4 py-2.5 capitalize">{o.type}</td>
                <td className="px-4 py-2.5">
                  {o.customer_name || (o.table_id ? "Table" : "—")}
                </td>
                <td className="px-4 py-2.5 text-right font-mono-num">
                  {(o.items || []).length}
                </td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">
                  {inr(o.total)}
                </td>
                <td className="px-4 py-2.5">{statusBadge(o.status)}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => setView(o)}
                    data-testid={`view-${o.id}`}
                  >
                    <Eye size={13} />
                  </Button>
                  {o.status !== "paid" && o.status !== "cancelled" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-[#E76F51]"
                      onClick={() => cancel(o)}
                    >
                      <XCircle size={13} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-[#7A7873]"
                >
                  No orders.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="bg-white max-w-md p-0">
          {view && <Invoice order={view} />}
          <div className="flex gap-2 p-4 border-t border-[#EBE8E2] no-print">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setView(null)}
            >
              Close
            </Button>
            <Button
              className="flex-1 bg-[#D95D39] hover:bg-[#C24F2C]"
              onClick={() => window.print()}
            >
              <Printer size={16} className="mr-1.5" /> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
